import React from 'react';
import classnames from 'classnames';
import { useIntl, useLocation } from 'umi';
import { Alert } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import useCOS, { getUseAccelerateByENV } from '@gp/hooks/useCos/useCOS';
import useLowcodeCosTemplate from './_hooks/useLowcodeCosTemplate';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';

import type { IFetchTencentQCouldMessage } from '@intelligence/services/cos';

import './index.scss';

export interface ILowCodeTemplateProps extends IFetchTencentQCouldMessage {
  config: IRecord;
}

const LowCodeTemplate: React.FC<ILowCodeTemplateProps> = (props) => {
  const { config, fetchTencentQCouldMessage } = props;

  const location = useLocation();
  const { locale } = useIntl();

  // Get parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const title = searchParams.get('title');

  const useAccelerate = getUseAccelerateByENV();

  const { isCOSInited, getObject } = useCOS({
    useAccelerate,
    baseCOSConfigFetcher: fetchTencentQCouldMessage,
    authorizationFetcher: fetchTencentQCouldMessage,
  });

  const { loading, error, htmlContentMap } = useLowcodeCosTemplate({
    templateConfig: config,
    isCOSInited,
    getObject,
  });

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 180px)',
          color: '#262832',
        }}
      >
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbi-cos-page-error">
        <Alert>Failed to load content: {error instanceof Error ? error.message : 'Unknown error'}</Alert>
      </div>
    );
  }

  return (
    <div className="dbi-cos-page-wrapper">
      {(config?.title || title) && <h1 className="dbi-cos-page-title">{config?.title || title}</h1>}
      <div className="dbi-cos-page-container">
        <iframe
          className={classnames('dbi-cos-content-iframe', { hide: locale === LANGUAGE_CN })}
          title="Low-code template content"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          srcDoc={htmlContentMap?.[LANGUAGE_EN]}
        />
        <iframe
          className={classnames('dbi-cos-content-iframe', { hide: locale === LANGUAGE_EN })}
          title="Low-code template content"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          srcDoc={htmlContentMap?.[LANGUAGE_CN]}
        />
      </div>
    </div>
  );
};

export default LowCodeTemplate;
